import { useRef } from "react";
import { useBenchmarkAlignment } from "../../../hooks/useBenchmarkAlignment";
import BenchmarkCard from "../shared/BenchmarkCard";
import { bandStyle, CRITICAL, GOOD, MUTED, MUTED_2, sectionMetaStyle, sectionTitleStyle } from "../shared/styles";

const OVERALL_METRIC = {
  label: "CO₂-Intensität",
  description: "Heizung, Strom, Treibstoff",
  unit: "kg CO₂/produktive Stunden",
  unitShort: "kg CO₂/prod. h",
  digits: 3,
  lowerIsBetter: true
};
const REPARATURQUOTE_METRIC = { label: "Reparaturquote", unit: "%", digits: 0, lowerIsBetter: false, scaleMax: 100 };
const REPAIR_QUOTA_PLACEHOLDER_CATEGORIES = [
  { key: "stossstangen", label: "Stossstangen", unit: "%", digits: 0, lowerIsBetter: false },
  { key: "tueren", label: "Türen", unit: "%", digits: 0, lowerIsBetter: false },
  { key: "rahmenteile", label: "Rahmenteile", unit: "%", digits: 0, lowerIsBetter: false },
  { key: "fensterscheiben", label: "Fensterscheiben", unit: "%", digits: 0, lowerIsBetter: false },
  { key: "lichter", label: "Lichter", unit: "%", digits: 0, lowerIsBetter: false }
];

// Subtle raw-KPI annotations shown under the matching CI category row (not their own cards).
const CATEGORY_ANNOTATIONS = {
  "Heizung Gebäude": () => "exkl. Kabine",
  "Heizung Kabine": (_fleet, waterBasePaintPct) =>
    waterBasePaintPct != null ? `Wasserbasislack-Anteil: ${waterBasePaintPct.toFixed(0)}%` : null
};

export default function CO2IntensityEnergySection({ co2Intensity, measures, fleetElectrificationPct, waterBasePaintPct, repairQuota }) {
  const sectionRef = useRef(null);
  useBenchmarkAlignment(sectionRef);
  const categoryAnnotations = {};
  Object.entries(CATEGORY_ANNOTATIONS).forEach(([label, resolve]) => {
    const text = resolve(fleetElectrificationPct, waterBasePaintPct);
    if (text) categoryAnnotations[label] = text;
  });

  const repairQuoteValue =
    repairQuota != null
      ? { betrieb: repairQuota, branchenschnitt: null, top25: null }
      : null;

  return (
    <section ref={sectionRef} style={bandStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", alignItems: "baseline" }}>
        <div>
          <h2 className="h2" style={sectionTitleStyle}>Scoring</h2>
          <p style={sectionMetaStyle}>Ihr Betrieb im Vergleich zur Branche</p>
        </div>
        <div style={{ display: "flex", gap: "1rem", fontSize: "0.74rem", color: MUTED, flexWrap: "wrap" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <span
              style={{
                width: 14,
                height: 8,
                borderRadius: 2,
                display: "inline-block",
                background: `linear-gradient(to right, ${GOOD}, ${CRITICAL})`
              }}
            />{" "}
            Betrieb
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <span style={{ width: 2, height: 10, background: MUTED_2, display: "inline-block" }} /> Durchschnitt
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
            <span
              style={{
                width: 3,
                height: 10,
                display: "inline-block",
                background: `repeating-linear-gradient(to bottom, ${GOOD} 0px, ${GOOD} 3px, transparent 3px, transparent 5px)`
              }}
            />{" "}
            Top 25%
          </span>
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem", alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <BenchmarkCard
            metric={OVERALL_METRIC}
            value={co2Intensity?.overall}
            categories={co2Intensity?.categories}
            measures={measures}
            categoryAnnotations={categoryAnnotations}
          />
        </div>
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <BenchmarkCard
            metric={REPARATURQUOTE_METRIC}
            value={repairQuoteValue}
            categories={REPAIR_QUOTA_PLACEHOLDER_CATEGORIES}
            disabled
            disabledLabel="noch nicht verfügbar"
          />
        </div>
      </div>

    </section>
  );
}
